import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Axios from '../../api/axios';
import notify from '../../utils/notifyUtil';
import Button from 'devextreme-react/button';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import '../RAPOR_WHATSAPP_GORUSME/rapor_whatsapp_gorusme.scss';
import './rapor_whatsapp_toplu.scss';

const fmt = (v) => (v ? new Date(v).toLocaleString('tr-TR') : '—');

function highlightText(text, q) {
  const raw = String(text || '');
  const needle = String(q || '').trim();
  if (!needle) return raw;
  const lower = raw.toLowerCase();
  const n = needle.toLowerCase();
  const parts = [];
  let i = 0;
  while (i < raw.length) {
    const at = lower.indexOf(n, i);
    if (at < 0) {
      parts.push(raw.slice(i));
      break;
    }
    if (at > i) parts.push(raw.slice(i, at));
    parts.push(<mark key={`${at}-${i}`} className="wa-bubble-hit">{raw.slice(at, at + needle.length)}</mark>);
    i = at + needle.length;
  }
  return parts.length ? parts : raw;
}

function phoneOf(row) {
  return row?.WTG_TELEFON || row?.AI_PHONE || row?.WEC_PHONE || row?.WA_TEL || row?.CAR_GSM || row?.CAR_TEL1 || '';
}

/**
 * WhatsApp sağ band: içerik araması, görüşme, agent atama, kuyruktan düş, AI el değiştirme.
 * queueMode: gonderim (WTG satırı), ai (AI kuyruğu), auto (önce WTG, yoksa AI).
 */
export default function WhatsAppSagPanel({
  row,
  users = [],
  canEdit = false,
  queueMode = 'auto',
  onClose,
  onRemoved,
  onAssigned,
}) {
  const [conversations, setConversations] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [agentRef, setAgentRef] = useState(null);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const loadChat = useCallback(async (current) => {
    if (!current?.CAR_REF) {
      setConversations([]);
      return;
    }
    setChatLoading(true);
    try {
      const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_KART/whatsappConversations`, {
        params: { CAR_REF: current.CAR_REF, phone: phoneOf(current), limit: 500, syncResim: '0' },
      });
      setConversations(res.data?.conversations || []);
    } catch (err) {
      setConversations([]);
      notify(err?.response?.data?.message || err.message, 'error', 2500);
    } finally {
      setChatLoading(false);
    }
  }, []);

  useEffect(() => {
    setAgentRef(row?.CAR_ARA_KUL_REF || null);
    setMessage('');
    setSearch('');
    loadChat(row);
  }, [row, loadChat]);

  const visible = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    return conversations.map((conv) => ({
      ...conv,
      messages: (conv.messages || []).filter((m) => {
        if (!q) return true;
        return `${m.body || ''} ${m.buttonLabel || ''}`.toLowerCase().includes(q);
      }),
    })).filter((conv) => !q || conv.messages.length);
  }, [conversations, search]);

  const activeConv = conversations[0];
  const agents = (users || []).filter((u) => Number(u?.KUL_REF) > 0);

  const assign = async () => {
    if (!row?.CAR_REF || !agentRef) return;
    setSaving(true);
    try {
      await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/whatsapp_gonderim_kuyruk`, {
        action: 'assign', carRef: row.CAR_REF, agentRef,
      });
      notify('Agent atandı', 'success', 1800);
      onAssigned?.();
    } catch (err) {
      notify(err?.response?.data?.message || err.message, 'error', 2500);
    } finally {
      setSaving(false);
    }
  };

  const removeFromQueue = async () => {
    const wtgRef = Number(row?.WTG_REF);
    const useGonderim = queueMode === 'gonderim' || (queueMode === 'auto' && wtgRef > 0);
    setSaving(true);
    try {
      if (useGonderim) {
        if (!(wtgRef > 0)) {
          notify('Bu satır gönderim kuyruğunda değil', 'warning', 2200);
          return;
        }
        await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/whatsapp_gonderim_kuyruk`, {
          action: 'remove', ref: wtgRef,
        });
        notify('Gönderim kuyruktan düşürüldü. WhatsApp mesajı geri alınmaz.', 'success', 2200);
      } else {
        await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/ai_whatsapp_kuyruk_cikar`, { carRef: row.CAR_REF });
        notify('Cari AI WhatsApp kuyruğundan çıkarıldı', 'success', 1800);
      }
      onRemoved?.();
    } catch (err) {
      notify(err?.response?.data?.message || err.message, 'error', 3000);
    } finally {
      setSaving(false);
    }
  };

  const setAi = async (enabled) => {
    if (!activeConv) return notify('WhatsApp konuşması bulunamadı', 'warning', 2000);
    setSaving(true);
    try {
      await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/whatsapp_gonderim_ai`, {
        conversationId: activeConv.id, enabled,
      });
      notify(enabled ? 'AI devreye alındı' : 'AI devreden çıkarıldı', 'success', 1800);
      await loadChat(row);
    } catch (err) {
      notify(err?.response?.data?.message || err.message, 'error', 2500);
    } finally {
      setSaving(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !row) return;
    setSaving(true);
    try {
      await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/whatsapp_gonderim_mesaj`, {
        phone: phoneOf(row), contactName: row.CAR_ADSOY, body: message.trim(),
      });
      setMessage('');
      notify('Mesaj gönderim kuyruğuna alındı', 'success', 1800);
      setTimeout(() => loadChat(row), 700);
    } catch (err) {
      notify(err?.response?.data?.message || err.message, 'error', 2500);
    } finally {
      setSaving(false);
    }
  };

  const title = row ? (row.CAR_ADSOY || phoneOf(row) || `Cari ${row.CAR_REF}`) : 'WhatsApp görüşmesi';
  const meta = [row?.GONDERIM_ETIKET, row?.GONDERIM_SABLON || row?.WEC_TEMPLATE || row?.WTG_SABLON, row?.SABLON_GONDERIM ? `Aynı şablon ${row.SABLON_GONDERIM}` : '', row?.GORULME, row?.GONDERIM_HATA, row?.BUTON || row?.WEC_TITLE]
    .filter((part) => part && part !== 'Cevapsız')
    .join(' · ');

  return (
    <aside className="wa-gorusme-panel wa-sag-panel">
      <div className="wa-gorusme-panel-head">
        <strong>{title}</strong>
        <div>
          {row && <Button text="Yenile" stylingMode="outlined" disabled={chatLoading || saving} onClick={() => loadChat(row)} />}
          {onClose && <Button text="Kapat" stylingMode="text" onClick={onClose} />}
        </div>
      </div>
      {meta ? <p className="wa-sag-meta">{meta}</p> : null}
      <div className="wa-gorusme-panel-search">
        <input
          type="search"
          value={search}
          placeholder="Görüşme içeriğinde ara"
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Görüşme içeriğinde ara"
        />
      </div>
      <div className="wa-sent-actions">
        <SelectBox
          dataSource={agents}
          displayExpr="KUL_ADI"
          valueExpr="KUL_REF"
          searchEnabled
          showClearButton
          placeholder="Agent seç"
          value={agentRef}
          onValueChanged={(e) => setAgentRef(e.value)}
          disabled={!row || !canEdit || saving}
        />
        <Button text="Agent ata" type="default" disabled={!row || !agentRef || !canEdit || saving} onClick={assign} />
        <Button text="Kuyruktan düş" type="danger" disabled={!row || !canEdit || saving} onClick={removeFromQueue} />
        <Button text="AI devreden çıkar" disabled={!activeConv || activeConv.handoffState === 'HUMAN' || !canEdit || saving} onClick={() => setAi(false)} />
        <Button text="AI devreye al" disabled={!activeConv || activeConv.handoffState === 'AI' || !canEdit || saving} onClick={() => setAi(true)} />
      </div>
      <div className="wa-gorusme-chat">
        {!row && <div className="wa-gorusme-empty">Soldan bir kayıt seçin.</div>}
        {chatLoading && <div className="wa-gorusme-empty">Görüşmeler yükleniyor…</div>}
        {!chatLoading && row && !conversations.length && <div className="wa-gorusme-empty">Henüz görüşme bulunamadı.</div>}
        {!chatLoading && row && conversations.length > 0 && !visible.length && (
          <div className="wa-gorusme-empty">Bu içerik görüşmede yok.</div>
        )}
        {visible.map((conv) => (
          <div key={conv.id} className="wa-chat-stack">
            <div className="wa-conv-label">{conv.phone} · AI: {conv.handoffState === 'HUMAN' ? 'kapalı' : 'açık'}</div>
            {(conv.messages || []).map((m) => {
              const inbound = String(m.direction).toUpperCase() === 'INBOUND';
              return (
                <div
                  key={m.id || m.messageId}
                  className={`wa-bubble ${inbound ? 'wa-bubble--in' : 'wa-bubble--out'}`}
                  style={{ alignSelf: inbound ? 'flex-start' : 'flex-end' }}
                >
                  <div className="wa-bubble-body">{highlightText(m.body || m.buttonLabel || '[medya]', search)}</div>
                  {m.buttonLabel ? <div className="wa-bubble-meta">Buton: {m.buttonLabel}</div> : null}
                  <div className="wa-bubble-meta">
                    {fmt(m.createdAt)} · {inbound ? 'gelen' : 'giden'}{m.status ? ` · ${m.status}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="wa-sent-compose">
        <TextBox
          value={message}
          valueChangeEvent="keyup"
          placeholder="WhatsApp mesajı yazın…"
          onValueChanged={(e) => setMessage(e.value || '')}
          disabled={!row || !canEdit || saving}
          onEnterKey={sendMessage}
        />
        <Button text="Gönder" type="success" disabled={!row || !message.trim() || !canEdit || saving} onClick={sendMessage} />
      </div>
    </aside>
  );
}
